import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import yoproductsServiceClient from './yoproducts-service-client';
import { PER_MONTH, PER_YEAR, TOTAL, MONTH, API_TERM_VALUES } from '../constants/periods';

export const getRegistrationPriceValue = ({ prices, period, currency, priceType = 'current' }) =>
  get(prices, ['registration', period, 'prices', priceType, currency], '0.00');

export const getDomainPriceValue = ({ prices, tld, period, currency, priceType = 'current' }) =>
  get(prices, ['domain', tld, period, 'prices', priceType, currency], '0.00');

export const getPriceValue = ({ prices, plan, period, currency, priceType = 'current' }) =>
  get(prices, [plan, period, 'prices', priceType, currency], '0.00');

export const getPricePerMonth = (params, priceType = 'current') =>
  getPriceValue({ ...params, period: API_TERM_VALUES[PER_MONTH], priceType });

export const getPricePerYear = (params, priceType = 'current') =>
  getPriceValue({ ...params, period: API_TERM_VALUES[PER_YEAR], priceType });

export const getRegistrationPricePerYear = (params, priceType = 'current') =>
  getRegistrationPriceValue({ ...params, period: API_TERM_VALUES[PER_YEAR], priceType });

export const getDomainPricePerYear = (params, priceType = 'current') =>
  getDomainPriceValue({ ...params, period: API_TERM_VALUES[PER_YEAR], priceType });

const getPriceData = ({ prices, plan, currency, locale, isIntroDiscountAvailable = false }) => {
  if (isEmpty(prices)) {
    return {
      [PER_MONTH]: {
        [MONTH]: null,
      },
      [PER_YEAR]: {
        [TOTAL]: null,
        [MONTH]: null,
      },
    };
  }

  const yoproducts = yoproductsServiceClient.get();

  const hasDiscountedPrices =
    isIntroDiscountAvailable &&
    (Boolean(get(prices, [plan, API_TERM_VALUES[PER_MONTH], 'prices', 'discounted', currency])) ||
      Boolean(get(prices, [plan, API_TERM_VALUES[PER_YEAR], 'prices', 'discounted', currency])));
  const mainPriceType = hasDiscountedPrices ? 'discounted' : 'current';

  const pricePerMonth = getPricePerMonth({ prices, plan, currency }, mainPriceType);
  const pricePerYearTotal = getPricePerYear({ prices, plan, currency }, mainPriceType);
  const pricePerYearMonth = (pricePerYearTotal / 12).toFixed(2);

  // Get old prices (only if discount is active)
  const oldPricePerMonth = hasDiscountedPrices
    ? getPricePerMonth({ prices, plan, currency }, 'current')
    : null;
  const oldPricePerYearTotal = hasDiscountedPrices
    ? getPricePerYear({ prices, plan, currency }, 'current')
    : null;
  const oldPricePerYearMonth = hasDiscountedPrices ? (oldPricePerYearTotal / 12).toFixed(2) : null;

  const perMonthPriceParts = yoproducts.actions.getPriceParts(pricePerMonth, locale, currency, {
    leadingCurrency: true,
  });
  const perYearPriceTotalParts = yoproducts.actions.getPriceParts(
    pricePerYearTotal,
    locale,
    currency,
    { leadingCurrency: true }
  );
  const perYearPriceMonthParts = yoproducts.actions.getPriceParts(
    pricePerYearMonth,
    locale,
    currency,
    { leadingCurrency: true }
  );

  const oldPricePerMonthParts = oldPricePerMonth
    ? yoproducts.actions.getPriceParts(oldPricePerMonth, locale, currency, {
        leadingCurrency: true,
      })
    : null;
  const oldPricePerYearTotalParts = oldPricePerYearTotal
    ? yoproducts.actions.getPriceParts(oldPricePerYearTotal, locale, currency, {
        leadingCurrency: true,
      })
    : null;
  const oldPricePerYearMonthParts = oldPricePerYearMonth
    ? yoproducts.actions.getPriceParts(oldPricePerYearMonth, locale, currency, {
        leadingCurrency: true,
      })
    : null;

  const result = {
    [PER_MONTH]: {
      [MONTH]: perMonthPriceParts.value,
      parts: {
        [MONTH]: perMonthPriceParts,
      },
    },
    [PER_YEAR]: {
      [TOTAL]: perYearPriceTotalParts.value,
      [MONTH]: perYearPriceMonthParts.value,
      parts: {
        [TOTAL]: perYearPriceTotalParts,
        [MONTH]: perYearPriceMonthParts,
      },
    },
  };

  if (hasDiscountedPrices) {
    result[PER_MONTH].oldPrice = {
      [MONTH]: oldPricePerMonthParts.value,
      parts: {
        [MONTH]: oldPricePerMonthParts,
      },
    };
    result[PER_YEAR].oldPrice = {
      [TOTAL]: oldPricePerYearTotalParts.value,
      [MONTH]: oldPricePerYearMonthParts.value,
      parts: {
        [TOTAL]: oldPricePerYearTotalParts,
        [MONTH]: oldPricePerYearMonthParts,
      },
    };
  }

  return result;
};

export default getPriceData;
